import { useMemo } from 'react';
import type { MapData } from '../../types';
import { computeMonthlyAllocations, projectGrowth } from '../../utils/growthProjection';

interface GrowthChartProps {
  mapData: MapData;
}

const CHART_WIDTH = 600;
const CHART_HEIGHT = 200;
const PADDING = { top: 20, right: 20, bottom: 44, left: 20 };

export default function GrowthChart({ mapData }: GrowthChartProps) {
  const { points, hasAllocations } = useMemo(() => {
    const { monthlySavings, monthlyInvestments } = computeMonthlyAllocations(mapData);
    const total = monthlySavings + monthlyInvestments;
    if (total === 0) {
      return { points: [], hasAllocations: false };
    }
    const pts = projectGrowth(monthlySavings, monthlyInvestments);
    return { points: pts, hasAllocations: true };
  }, [mapData]);

  if (!hasAllocations) {
    return (
      <div className="growth-chart">
        <div className="growth-chart__header">
          <h3 className="growth-chart__title">Your Growth Trajectory</h3>
          <p className="growth-chart__subtitle">See how your money could grow with this setup</p>
        </div>
        <div className="growth-chart__empty">
          <p>Add savings or investment allocations to see your growth trajectory.</p>
        </div>
      </div>
    );
  }

  const plotW = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotH = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const finalValue = points[points.length - 1].value;
  const maxVal = finalValue * 1.15;

  const toX = (month: number) => PADDING.left + (month / 36) * plotW;
  const toY = (value: number) => PADDING.top + plotH - (value / maxVal) * plotH;

  // Build a smooth cubic bezier curve through the data points
  const pathData = points
    .map((p, i) => {
      const x = toX(p.month);
      const y = toY(p.value);
      if (i === 0) return `M ${x.toFixed(1)} ${y.toFixed(1)}`;
      const prev = points[i - 1];
      const px = toX(prev.month);
      const py = toY(prev.value);
      const dx = x - px;
      // Horizontal tangent handles for a smooth monotone curve
      const cp1x = px + dx * 0.5;
      const cp2x = x - dx * 0.5;
      return `C ${cp1x.toFixed(1)} ${py.toFixed(1)} ${cp2x.toFixed(1)} ${y.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  const areaData =
    pathData +
    ` L ${toX(36).toFixed(1)} ${(PADDING.top + plotH).toFixed(1)}` +
    ` L ${PADDING.left.toFixed(1)} ${(PADDING.top + plotH).toFixed(1)} Z`;

  const pathLength = points.length * 20;

  return (
    <div className="growth-chart">
      <div className="growth-chart__header">
        <h3 className="growth-chart__title">Your Growth Trajectory</h3>
        <p className="growth-chart__subtitle">See how your money could grow with this setup</p>
      </div>
      <div className="growth-chart__container">
        <svg
          className="growth-chart__svg"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* X-axis baseline */}
          <line
            x1={PADDING.left}
            y1={PADDING.top + plotH}
            x2={PADDING.left + plotW}
            y2={PADDING.top + plotH}
            stroke="var(--color-gray-200)"
          />

          {/* X-axis labels - vague */}
          <text
            x={toX(0)}
            y={PADDING.top + plotH + 22}
            textAnchor="start"
            fill="var(--color-text-tertiary)"
            fontSize="12"
          >
            Today
          </text>
          <text
            x={toX(36)}
            y={PADDING.top + plotH + 22}
            textAnchor="end"
            fill="var(--color-text-tertiary)"
            fontSize="12"
          >
            Your future
          </text>

          {/* Upward arrow at right end */}
          <line
            x1={PADDING.left + plotW + 2}
            y1={PADDING.top + plotH - 4}
            x2={PADDING.left + plotW + 2}
            y2={PADDING.top + 20}
            stroke="var(--color-gray-300)"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          <polygon
            points={`${PADDING.left + plotW - 3},${PADDING.top + 24} ${PADDING.left + plotW + 2},${PADDING.top + 14} ${PADDING.left + plotW + 7},${PADDING.top + 24}`}
            fill="var(--color-gray-300)"
          />

          {/* Area fill */}
          <path d={areaData} fill="url(#areaGradient)" />

          {/* Main line with drawing animation */}
          <path
            className="growth-chart__line"
            d={pathData}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLength}
            strokeDashoffset={pathLength}
          />

          {/* Endpoint glow dot */}
          <circle
            className="growth-chart__dot"
            cx={toX(36)}
            cy={toY(finalValue)}
            r="5"
            fill="var(--color-accent)"
            opacity="0.3"
          />
          <circle
            className="growth-chart__dot"
            cx={toX(36)}
            cy={toY(finalValue)}
            r="3"
            fill="var(--color-accent)"
          />
        </svg>
      </div>
    </div>
  );
}
